// =====================================================================
// src/screens/v2/ManageBusinessTab.jsx
//
// Phase 6.0c — Manage hub for the active business inside MerchantApp.
// Refactored against the 6-item polish spec:
//
//   1. Aangan Connect card — info copy + "Chat with Aangan support"
//      CTA (wired in Session 5a — opens the real support chat modal).
//      Trial / subscription details are surfaced separately by the
//      SubscriptionBanner on the Today tab.
//   2. Business info / Address & delivery rows — open existing
//      edit overlays (unchanged).
//   3. Business hours — full editor overlay (BusinessHoursEditor)
//      that lets the owner pick standard / custom / 24-7 / appointment
//      modes and persists to the `hours` field on the biz doc.
//   4. Products & menu + Photo gallery sections — REMOVED. Catalog tab
//      and the biz-info edit overlay already cover those surfaces.
//   5. Order Alerts & Contact — three inline fields:
//        - Phone (customer-facing call number)
//        - WhatsApp (customer-facing WA number)
//        - Registered mobile (read-only, from business.phone)
//      All three default to `business.phone` (the registration number).
//      Phone + WhatsApp can be overridden and persist to
//      `customerPhone` / `customerWhatsapp` on the biz doc.
//      Push notifications row removed.
//   6. Support — Email (mailto), Chat (live, Session 5a), Help & FAQ
//      (static expandable list with links back to email/chat).
//   7. Report an issue — REMOVED in Session A. Support chat handles bug
//      reports; abuse in a batch is reported via V1's ReportUserSheet.
//      Contextual reporting of V2 wall posts / order chats is a separate
//      session, gated on V2 going live on Android.
// =====================================================================

import React, { useState, useEffect } from 'react';
import {
  Briefcase, Clock, MapPin, ChevronRight, ExternalLink, MessageSquare,
  HelpCircle, LogOut, Mail, Phone, MessageCircle, Check, X,
  ChevronDown, ChevronUp, FileCheck, Camera, ShieldCheck,
} from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { logoBgFromCategory } from '../../lib/v2/firestore';
// Item #8 — FSSAI cert reupload reuses the same storage helper +
// validator used by the registration wizard. fssaiVerified gets reset
// to false whenever the merchant edits the number or replaces the
// cert (admin re-review needed) — same intent as the registration
// behaviour of writing fssaiVerified=false on initial create.
import { uploadFssaiCert, validateImageFile } from '../../lib/v2/storagePhotos';
import { openOrCreateSupportChat } from '../../lib/db';
// Session 6c2a — merchant-side gating on inline edits when biz is inactive.
import { isBusinessActive } from '../../lib/v2/isBusinessActive';
import BusinessHoursEditor from './BusinessHoursEditor';
import SupportChatModal from './SupportChatModal';
import './ManageBusinessTab.css';

const SUPPORT_EMAIL = 'info@aapsaathi.com';

// Categories that require FSSAI under Indian food-safety law. Mirrors
// the `food: true` flag in RegisterBusiness's CATEGORIES array — kept
// in sync manually for now; pull into a shared helper if a third
// surface starts needing the same check.
const FOOD_CATEGORIES = new Set(['food_beverages', 'dairy', 'bakery', 'handmade']);

const CATEGORY_LABEL = {
  food_beverages: 'Food & Beverages',
  apparel: 'Fashion & Apparel',
  services: 'Services',
  groceries: 'Groceries',
  electronics: 'Electronics',
  health_beauty: 'Health & Beauty',
  home_decor: 'Home & Décor',
};
function labelForCategory(cat) {
  return CATEGORY_LABEL[cat] || 'Other';
}

// Static FAQ entries — small enough to keep inline. Update copy here as
// the merchant experience evolves.
const FAQS = [
  {
    q: 'How do customers find my business?',
    a: 'Customers discover you through the Business Directory (browseable by category and location) and via direct shares of your business link.',
  },
  {
    q: 'How do orders work?',
    a: 'Customers tap "Place order" on your profile and pick items from your catalog. You receive the order in the Orders tab and use chat to coordinate payment, pickup, or delivery.',
  },
  {
    q: 'Do I pay Aangan a commission on orders?',
    a: 'No. Aangan is a listing and communication platform — payments happen directly between you and your customer (UPI, cash, or your preferred method).',
  },
  {
    q: 'Can I edit my business hours?',
    a: 'Yes. Tap "Business hours" above to set standard hours, per-day custom hours, or mark yourself as 24/7 or by-appointment.',
  },
  {
    q: 'How do I add photos of my products?',
    a: 'Go to the Catalog tab, tap a product, and use the photo upload option to add images.',
  },
];

export default function ManageBusinessTab({
  business,
  currentUser,
  onEdit,
  onViewPublicProfile,
  onSwitchToConsumer,
  onSetToast,
}) {
  // ---- Overlay state ----
  const [hoursEditorOpen, setHoursEditorOpen] = useState(false);

  // ---- Inline editors for customer-contact fields ----
  // Keyed by field name so only one row is in edit mode at a time.
  // null means no row is editing; a string ('customerPhone' etc) opens
  // that row's input.
  const [editingField, setEditingField] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [savingField, setSavingField] = useState(false);

  // ---- Optimistic local mirror for just-saved values ----
  // MerchantApp does not subscribe to the active business doc via
  // onSnapshot, so after a successful updateDoc the `business` prop
  // remains stale until the user navigates away or refreshes. Without a
  // local mirror, the row reverts to the pre-edit value the moment the
  // editor closes, which looks broken. We keep a per-field override
  // here that wins over `business.*` until the prop catches up, and we
  // wipe it whenever the business switches (so a stale override from a
  // different biz can't bleed across).
  const [pendingOverrides, setPendingOverrides] = useState({});
  useEffect(() => {
    setPendingOverrides({});
  }, [business?.id]);

  // ---- FAQ accordion state ----
  const [expandedFaq, setExpandedFaq] = useState(null);

  if (!business) {
    return (
      <div className="v2m-tab-scroll">
        <div className="v2m-mng-empty">No business to manage yet.</div>
      </div>
    );
  }

  // Session 6c2a — Derive whether the biz is currently active (paid
  // window OR trial window). When inactive, edit-affordances on the
  // settings rows are gated: existing values stay readable but tapping
  // a row does nothing. The Aangan Support row + entire Support
  // section stay live so the owner can always reach us to reactivate.
  // Backend enforcement of the same gate lands in Session 6b (rules).
  const bizActive = isBusinessActive(business);
  const editGated = !bizActive;

  const categoryStr = labelForCategory(business.category);
  const addressStr = [business.locality, business.city].filter(Boolean).join(', ') || 'No address set';
  // The phone captured during business registration is the canonical
  // "registered mobile" — stored as `business.phone` on the biz doc.
  // All three contact rows default to it so a freshly-registered
  // merchant immediately has sensible numbers shown for customers to
  // reach them at. The owner can later override Phone or WhatsApp if
  // they want a different customer-facing line. Registered mobile
  // always reflects the registration value (read-only).
  //
  // `pendingOverrides` wins over the biz doc when present so a freshly
  // saved value appears in the UI immediately rather than waiting for
  // the parent prop to refresh.
  const customerPhoneRaw = pendingOverrides.customerPhone !== undefined
    ? pendingOverrides.customerPhone
    : business.customerPhone;
  const customerWhatsappRaw = pendingOverrides.customerWhatsapp !== undefined
    ? pendingOverrides.customerWhatsapp
    : business.customerWhatsapp;

  const registeredMobile = business.phone || '';
  const customerPhone = (customerPhoneRaw && customerPhoneRaw.trim())
    || business.phone || '';
  const customerWhatsapp = (customerWhatsappRaw && customerWhatsappRaw.trim())
    || business.phone || '';

  // ---- Item #8 — FSSAI compliance values ----
  // isFood gates whether the entire LICENSES section renders. The
  // FSSAI number/cert/verified-status all flow through pendingOverrides
  // so saves reflect in the UI without waiting for the parent prop.
  const isFood = FOOD_CATEGORIES.has(business.category);
  const fssaiNumberRaw = pendingOverrides.fssaiNumber !== undefined
    ? pendingOverrides.fssaiNumber
    : business.fssaiNumber;
  const fssaiCertUrlRaw = pendingOverrides.fssaiCertUrl !== undefined
    ? pendingOverrides.fssaiCertUrl
    : business.fssaiCertUrl;
  const fssaiVerifiedRaw = pendingOverrides.fssaiVerified !== undefined
    ? pendingOverrides.fssaiVerified
    : business.fssaiVerified;
  const fssaiNumber = (fssaiNumberRaw || '').trim();
  const hasFssaiCert = !!fssaiCertUrlRaw;
  const fssaiVerified = !!fssaiVerifiedRaw;

  // Start editing a customer-contact field. Prefills with current value.
  function startEdit(field, currentValue) {
    setEditingField(field);
    setEditingDraft(currentValue || '');
  }

  function cancelEdit() {
    setEditingField(null);
    setEditingDraft('');
  }

  async function saveEdit() {
    if (savingField) return;
    setSavingField(true);
    try {
      // Light client-side validation — strip whitespace, allow digits + +
      const cleaned = editingDraft.trim();

      // Item #8 — FSSAI number gets stricter validation. Non-empty
      // value must match the 14-digit format (same regex registration
      // uses). Empty string is allowed (clears the field) since the
      // merchant may register without an FSSAI number and later choose
      // not to set one — we don't force the field once they've started.
      if (editingField === 'fssaiNumber' && cleaned && !/^\d{14}$/.test(cleaned)) {
        onSetToast?.('FSSAI number should be 14 digits');
        setSavingField(false);
        return;
      }

      const patch = {
        [editingField]: cleaned,
        updatedAt: serverTimestamp(),
      };
      // Editing the FSSAI number invalidates the prior admin review —
      // re-set fssaiVerified to false so the verified badge disappears
      // until the new number is approved.
      if (editingField === 'fssaiNumber') {
        patch.fssaiVerified = false;
      }
      await updateDoc(doc(db, 'businesses', business.id), patch);
      // Optimistically mirror the saved value so the row shows the new
      // number the instant the editor closes — without this, the UI
      // reverts to the stale `business.*` value from props until the
      // parent re-fetches the doc.
      setPendingOverrides(prev => ({ ...prev, [editingField]: cleaned }));
      onSetToast?.('Updated');
      setEditingField(null);
      setEditingDraft('');
    } catch (err) {
      console.error('[ManageBusinessTab] save field failed:', err);
      onSetToast?.(`Couldn't save: ${err.code || err.message || 'try again'}`);
    } finally {
      setSavingField(false);
    }
  }

  function handleEmailSupport() {
    const subject = encodeURIComponent(`Merchant support — ${business.name || 'Aangan business'}`);
    const body = encodeURIComponent(
      `Business: ${business.name || ''}\nBusiness ID: ${business.id || ''}\n\nDescribe your issue:\n\n`
    );
    if (typeof window === 'undefined') return;
    // Gmail web compose URL — opens in a new tab regardless of the
    // user's default mail-handler config. Many Windows users without a
    // registered mail client see nothing when mailto: fires; Gmail's
    // web compose endpoint works in any browser.
    const gmailUrl =
      `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(SUPPORT_EMAIL)}`
      + `&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank', 'noopener,noreferrer');
  }

  // ---- Support chat state (Session 5a) ----
  const [supportChatId, setSupportChatId] = useState(null);
  const [supportChatOpening, setSupportChatOpening] = useState(false);

  function handleChatSupport() {
    // Wired in Session 5a — opens the support chat modal with a chat
    // between this business owner and Aangan Support. openOrCreateSupportChat
    // is idempotent (deterministic chatId via makeChatId) so repeated
    // taps reopen the same chat.
    //
    // currentUser.name is passed so the helper can denormalize it onto
    // the chat doc (ownerName field) — lets the admin Support Inbox
    // render a human name in the chat list without an extra /users read
    // per row. The helper refreshes ownerName on each open, so if the
    // owner renames themselves the chat's display name follows.
    if (supportChatOpening || !currentUser?.id) return;
    setSupportChatOpening(true);
    openOrCreateSupportChat({
      ownerUid: currentUser.id,
      ownerName: currentUser.name || '',
      // Session A — marks the chat doc openedFrom:'merchant' so the admin
      // Support Inbox knows to offer the Aangan Connect plan-card picker.
      // One-way upgrade: a chat first opened from V1 flips to 'merchant'
      // the moment its owner opens support from here.
      source: 'merchant',
    })
      .then(({ chatId }) => {
        setSupportChatId(chatId);
      })
      .catch((err) => {
        console.error('[ManageBusinessTab] open support chat failed:', err);
        onSetToast?.('Could not open Aangan Support chat. Please try again.');
      })
      .finally(() => {
        setSupportChatOpening(false);
      });
  }

  // Item #8 — FSSAI certificate upload. Reuses the same storage helper
  // and validator the registration wizard uses, so the file shape and
  // size limits are identical. A successful upload resets
  // fssaiVerified to false because admin needs to re-review the new
  // cert before the verified badge can return.
  async function handleFssaiCertPicked(file) {
    if (!file || !business?.id || !currentUser?.id) return;
    try {
      validateImageFile(file);
      onSetToast?.('Uploading certificate…');
      const url = await uploadFssaiCert(business.id, file, currentUser.id);
      await updateDoc(doc(db, 'businesses', business.id), {
        fssaiCertUrl: url,
        fssaiVerified: false,
        updatedAt: serverTimestamp(),
      });
      // Optimistic mirror so the "Certificate uploaded" status is shown
      // immediately even if MerchantApp hasn't re-fetched the doc yet.
      setPendingOverrides(prev => ({
        ...prev,
        fssaiCertUrl: url,
        fssaiVerified: false,
      }));
      onSetToast?.('Certificate uploaded — pending review');
    } catch (err) {
      console.error('[ManageBusinessTab] FSSAI cert upload failed:', err);
      onSetToast?.(err?.message || "Couldn't upload certificate. Try again.");
    }
  }

  // ---- Renderers ----

  function renderRow({ icon: Icon, title, sub, onTap, gated }) {
    // Session 6c2a — When `gated`, the row still renders (owner needs
    // to see current values for context) but clicks do nothing and the
    // row visually reads as disabled. .v2m-mng-row.gated in CSS handles
    // the muted look + not-allowed cursor.
    if (gated) {
      return (
        <div className="v2m-mng-row gated" aria-disabled="true">
          <div className="v2m-mng-row-icon"><Icon size={18} /></div>
          <div className="v2m-mng-row-text">
            <div className="v2m-mng-row-title">{title}</div>
            <div className="v2m-mng-row-sub">{sub}</div>
          </div>
          <ChevronRight size={16} className="v2m-mng-row-chev" />
        </div>
      );
    }
    return (
      <button className="v2m-mng-row" onClick={onTap}>
        <div className="v2m-mng-row-icon"><Icon size={18} /></div>
        <div className="v2m-mng-row-text">
          <div className="v2m-mng-row-title">{title}</div>
          <div className="v2m-mng-row-sub">{sub}</div>
        </div>
        <ChevronRight size={16} className="v2m-mng-row-chev" />
      </button>
    );
  }

  // Customer-contact rows have an inline edit affordance — tap a row,
  // it transforms into [label + input + save/cancel] without leaving the
  // page. Cheaper than yet-another overlay for two short text fields.
  function renderContactRow({ field, icon: Icon, title, value, placeholder, readOnly, gated }) {
    // Session 6c2a — When `gated`, force readOnly semantics AND add the
    // .gated class so the visual matches renderRow(gated). The `readOnly`
    // path already handles muted styling (default cursor, no chevron,
    // '—' fallback for empty value) — the .gated class adds the extra
    // opacity treatment shared with the settings rows.
    const effectiveReadOnly = readOnly || gated;
    const isEditing = editingField === field;
    if (isEditing) {
      return (
        <div className="v2m-mng-row" style={{ alignItems: 'center', padding: '12px 14px' }}>
          <div className="v2m-mng-row-icon"><Icon size={18} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="v2m-mng-row-title" style={{ marginBottom: 6 }}>{title}</div>
            <input
              type="tel"
              value={editingDraft}
              onChange={e => setEditingDraft(e.target.value)}
              placeholder={placeholder}
              autoFocus
              style={{
                width: '100%', padding: '7px 10px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--bg)',
                color: 'var(--ink)', fontSize: 13, outline: 'none', fontFamily: 'inherit',
              }}
            />
          </div>
          <button onClick={saveEdit} disabled={savingField}
                  style={{
                    width: 30, height: 30, borderRadius: 999, background: 'var(--ink)',
                    color: '#fff', border: 'none', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', marginLeft: 6,
                    opacity: savingField ? 0.6 : 1,
                  }} aria-label="Save">
            <Check size={14} />
          </button>
          <button onClick={cancelEdit}
                  style={{
                    width: 30, height: 30, borderRadius: 999, background: 'transparent',
                    color: 'var(--ink-soft)', border: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', marginLeft: 4,
                  }} aria-label="Cancel">
            <X size={14} />
          </button>
        </div>
      );
    }
    return (
      <button
        className={`v2m-mng-row${gated ? ' gated' : ''}`}
        onClick={effectiveReadOnly ? undefined : () => startEdit(field, value)}
        style={{ cursor: effectiveReadOnly ? 'default' : 'pointer' }}
        aria-disabled={gated ? 'true' : undefined}
      >
        <div className="v2m-mng-row-icon"><Icon size={18} /></div>
        <div className="v2m-mng-row-text">
          <div className="v2m-mng-row-title">{title}</div>
          <div className="v2m-mng-row-sub">
            {value || (effectiveReadOnly ? '—' : <span style={{ fontStyle: 'italic' }}>Tap to add</span>)}
          </div>
        </div>
        {!effectiveReadOnly && <ChevronRight size={16} className="v2m-mng-row-chev" />}
      </button>
    );
  }

  return (
    <div className="v2m-tab-scroll">
      <div className="v2m-mng-header">
        <div className="v2m-mng-title">Manage Business</div>
        <div className="v2m-mng-sub">Edit listing, hours, and settings</div>
      </div>

      {/* Business identity card */}
      <div className="v2m-mng-bizcard">
        <div
          className="v2m-mng-bizlogo"
          style={{
            background: business.logoImageUrl ? 'transparent' : logoBgFromCategory(business.category),
            overflow: 'hidden',
          }}
        >
          {business.logoImageUrl ? (
            <img src={business.logoImageUrl} alt=""
                 style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          ) : (
            business.logo || '🏪'
          )}
        </div>
        <div className="v2m-mng-bizidentity">
          <div className="v2m-mng-bizname">
            {business.name}
            {business.verified && (
              <span className="v2m-mng-verified" title="Verified by Aangan">✓</span>
            )}
          </div>
          <div className="v2m-mng-bizmeta">
            {categoryStr}
            {business.locality && <> · {business.locality}</>}
          </div>
          <button className="v2m-mng-public-link" onClick={onViewPublicProfile}>
            View public profile <ExternalLink size={11} style={{ marginLeft: 2 }} />
          </button>
        </div>
      </div>

      {/* ============ AANGAN CONNECT ============ */}
      {/* Copy updated after Sessions 5a/5c/6a/6d shipped the real
          activation loop: merchant chats support → admin sends an Aangan
          Connect plan card → merchant taps "I'd like this plan" → admin
          activates → merchant gets an FCM push + inbox notification.
          The old "Activation coming soon." string predated all of that
          and was actively misleading merchants about a live feature.
          The Chat CTA below is wired (Session 5a) — it opens the real
          support chat modal, it does not toast. The trial countdown lives
          on the Today tab's SubscriptionBanner; this is the static info
          card. */}
      <div className="v2m-mng-section-label">AANGAN CONNECT</div>
      <div className="v2m-mng-sub-card">
        <div className="v2m-mng-sub-text" style={{ fontSize: 13, lineHeight: 1.55 }}>
          Activate to let customers contact you and place orders.
          {' '}<strong style={{ color: 'var(--ink)' }}>Chat with us to get started.</strong>
        </div>
        <button
          onClick={handleChatSupport}
          style={{
            marginTop: 12, padding: '9px 14px', borderRadius: 999, fontSize: 13,
            fontWeight: 500, background: 'var(--ink)', color: '#fff', border: 'none',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: 'inherit',
          }}>
          <MessageCircle size={14} /> Chat with Aangan support
        </button>
      </div>

      {/* ============ LISTING ============ */}
      <div className="v2m-mng-section-label">LISTING</div>
      {editGated && (
        <div className="v2m-mng-inactive-hint">
          Reactivate your Aangan Connect to edit these details.
        </div>
      )}
      <div className="v2m-mng-rows">
        {renderRow({
          icon: Briefcase,
          title: 'Business info',
          sub: `${business.name} · ${categoryStr}`,
          onTap: () => onEdit('info'),
          gated: editGated,
        })}
        {renderRow({
          icon: Clock,
          title: 'Business hours',
          sub: hoursSummary(business.hours),
          onTap: () => setHoursEditorOpen(true),
          gated: editGated,
        })}
        {renderRow({
          icon: MapPin,
          title: 'Address & delivery',
          sub: addressStr,
          onTap: () => onEdit('address'),
          gated: editGated,
        })}
      </div>

      {/* ============ ORDER ALERTS & CONTACT ============ */}
      <div className="v2m-mng-section-label">ORDER ALERTS &amp; CONTACT</div>
      <div className="v2m-mng-rows">
        {renderContactRow({
          field: 'customerPhone',
          icon: Phone,
          title: 'Phone',
          value: customerPhone,
          placeholder: '+91 9000000000',
          gated: editGated,
        })}
        {renderContactRow({
          field: 'customerWhatsapp',
          icon: MessageCircle,
          title: 'WhatsApp',
          value: customerWhatsapp,
          placeholder: '+91 9000000000',
          gated: editGated,
        })}
        {renderContactRow({
          field: 'registeredMobile',
          icon: Phone,
          title: 'Registered mobile',
          value: registeredMobile,
          readOnly: true,
        })}
      </div>

      {/* ============ LICENSES (food categories only) ============ */}
      {/* Item #8 — Lets the merchant add or edit an FSSAI license
          number plus upload the certificate photo after registration.
          Registration treats both as optional, so this section is the
          merchant's path to add them later. Editing either invalidates
          the verified badge (admin re-review). */}
      {isFood && (
        <>
          <div className="v2m-mng-section-label">LICENSES</div>
          <div className="v2m-mng-rows">
            {/* FSSAI number — inline editable row, same pattern as the
                contact rows above. saveEdit applies the 14-digit
                validator when editingField === 'fssaiNumber'. */}
            {renderContactRow({
              field: 'fssaiNumber',
              icon: FileCheck,
              title: fssaiVerified
                ? 'FSSAI license · Verified ✓'
                : (fssaiNumber ? 'FSSAI license · Pending review' : 'FSSAI license'),
              value: fssaiNumber,
              placeholder: '14-digit FSSAI number',
              gated: editGated,
            })}
            {/* FSSAI certificate — file input wrapped in a label so each
                tap opens the picker without ref management. Reuses the
                product-card replace-photo pattern from item #6.
                Session 6c2a — when biz is inactive, render as a plain
                gated div so the file input can't be triggered. */}
            {editGated ? (
              <div className="v2m-mng-row gated" aria-disabled="true">
                <div className="v2m-mng-row-icon"><Camera size={18} /></div>
                <div className="v2m-mng-row-text">
                  <div className="v2m-mng-row-title">
                    {hasFssaiCert ? 'Replace FSSAI certificate' : 'Upload FSSAI certificate'}
                  </div>
                  <div className="v2m-mng-row-sub">
                    {hasFssaiCert
                      ? 'On file · visible only to Aangan review team'
                      : 'JPEG, PNG, or WebP · helps verification'}
                  </div>
                </div>
                <ChevronRight size={16} className="v2m-mng-row-chev" />
              </div>
            ) : (
              <label
                className="v2m-mng-row"
                aria-label={hasFssaiCert ? 'Replace FSSAI certificate' : 'Upload FSSAI certificate'}
                style={{ cursor: 'pointer' }}
              >
                <div className="v2m-mng-row-icon"><Camera size={18} /></div>
                <div className="v2m-mng-row-text">
                  <div className="v2m-mng-row-title">
                    {hasFssaiCert ? 'Replace FSSAI certificate' : 'Upload FSSAI certificate'}
                  </div>
                  <div className="v2m-mng-row-sub">
                    {hasFssaiCert
                      ? 'On file · visible only to Aangan review team'
                      : 'JPEG, PNG, or WebP · helps verification'}
                  </div>
                </div>
                <ChevronRight size={16} className="v2m-mng-row-chev" />
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) handleFssaiCertPicked(file);
                  }}
                />
              </label>
            )}
          </div>
          {/* Compliance hint — keeps the merchant oriented about what
              the field controls (the customer-visible verified badge). */}
          <div style={{
            padding: '6px 16px 14px', fontSize: 11, color: 'var(--ink-soft)',
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Required by Indian law for food businesses. The verified
              badge on your public profile appears after Aangan reviews
              your submission.
            </span>
          </div>
        </>
      )}

      {/* ============ SUPPORT ============ */}
      <div className="v2m-mng-section-label">SUPPORT</div>
      <div className="v2m-mng-rows">
        {renderRow({
          icon: Mail,
          title: 'Email support',
          sub: SUPPORT_EMAIL,
          onTap: handleEmailSupport,
        })}
        {renderRow({
          icon: MessageSquare,
          title: 'Chat support',
          sub: 'Live chat with our team',
          onTap: handleChatSupport,
        })}
      </div>

      {/* Help & FAQ — accordion. Each item is small enough to inline. */}
      <div className="v2m-mng-section-label">HELP &amp; FAQ</div>
      <div className="v2m-mng-rows">
        {FAQS.map((faq, i) => {
          const expanded = expandedFaq === i;
          return (
            <button
              key={i}
              className="v2m-mng-row"
              onClick={() => setExpandedFaq(expanded ? null : i)}
              style={{ alignItems: 'flex-start' }}
            >
              <div className="v2m-mng-row-icon"><HelpCircle size={18} /></div>
              <div className="v2m-mng-row-text">
                <div className="v2m-mng-row-title">{faq.q}</div>
                {expanded && (
                  <div style={{
                    marginTop: 6,
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    color: 'var(--ink-soft)',
                    whiteSpace: 'pre-wrap',
                  }}>
                    {faq.a}
                  </div>
                )}
              </div>
              {expanded
                ? <ChevronUp size={16} className="v2m-mng-row-chev" />
                : <ChevronDown size={16} className="v2m-mng-row-chev" />}
            </button>
          );
        })}
      </div>

      <div style={{
        padding: '8px 16px 14px', fontSize: 11, color: 'var(--ink-soft)',
      }}>
        Can't find what you need? <button onClick={handleEmailSupport}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent)', textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11 }}>
          Email us
        </button>
        {' '}or use Chat support above.
      </div>

      {/* Session A — REPORT AN ISSUE section removed. "Chat with Aangan
          support" (above, and in the Support section) is the single support
          channel: a merchant with a broken feature talks to a human rather
          than filing into a queue nobody triages. Abuse inside a batch is
          reported via V1's ReportUserSheet, which is group-scoped and routes
          to that batch's admins. Contextual reporting of V2 wall posts and
          order chats does not exist yet and MUST ship before V2 becomes
          reachable in the Android build. */}

      {/* ============ ACCOUNT ============ */}
      <div className="v2m-mng-section-label">ACCOUNT</div>
      <div className="v2m-mng-rows">
        <button className="v2m-mng-row" onClick={onSwitchToConsumer}>
          <div className="v2m-mng-row-icon"><LogOut size={18} /></div>
          <div className="v2m-mng-row-text">
            <div className="v2m-mng-row-title">Switch to consumer mode</div>
            <div className="v2m-mng-row-sub">Back to your community feed</div>
          </div>
          <ChevronRight size={16} className="v2m-mng-row-chev" />
        </button>
      </div>

      {/* Footer */}
      <div className="v2m-mng-footer">
        Aangan v2.0 · Merchant<br />
        Business platform, not a payment processor
      </div>

      {/* Hours editor overlay */}
      {hoursEditorOpen && (
        <BusinessHoursEditor
          business={business}
          onClose={() => setHoursEditorOpen(false)}
          onSetToast={onSetToast}
        />
      )}
      {supportChatId && (
        <SupportChatModal
          chatId={supportChatId}
          ownerUid={currentUser?.id}
          onClose={() => setSupportChatId(null)}
        />
      )}
    </div>
  );
}

// ---- Helpers ----

// Brief, human-readable hours summary for the "Business hours" row.
function hoursSummary(hours) {
  if (!hours || !hours.mode) return 'Set when you are open';
  if (hours.mode === '247') return 'Open 24/7';
  if (hours.mode === 'appointment') return 'By appointment';
  if (hours.mode === 'standard') {
    const days = Array.isArray(hours.days) ? hours.days : [];
    if (days.length === 0) return 'Closed';
    if (days.length === 7) return `${hours.open}–${hours.close}, every day`;
    if (days.length >= 5) return `${hours.open}–${hours.close}, ${days.length} days`;
    return `${hours.open}–${hours.close}, ${days.join(', ')}`;
  }
  if (hours.mode === 'custom') {
    const sched = hours.schedule || {};
    const openDays = Object.entries(sched).filter(([, v]) => v && !v.closed);
    if (openDays.length === 0) return 'All days closed';
    if (openDays.length === 7) return 'Open every day';
    return `Open ${openDays.length} days · custom hours`;
  }
  return 'Set when you are open';
}
